import React from 'react';
import { Box } from '@mantine/core';

import CompaniesTab from './companies/CompaniesTab';
import UnprocessedValuationUpdatesTab from 'src/secret-menu/bulk-valuations/UnprocessedValuationUpdatesTab';
import { Navigate, useParams } from 'react-router';
import TemplatesListPage from 'src/company/templates/components/TemplatesListPage';
import PageWithSidebar from 'src/UI/page-nav/PageWithSidebar';
import DeprecatedTemplateTab from 'src/secret-menu/templates/DeprecatedTemplateTab';
import FeatureFlagsTab from 'src/secret-menu/feature-flags/FeatureFlagsTab';
import CompanyUsersPage from 'src/secret-menu/company-users/CompanyUsersPage';
import EditorPlaygroundTab from './editor-playground/EditorPlaygroundTab';
import CompanyClientsPage from 'src/secret-menu/company-clients/CompanyClientsPage';
import FilesTab from 'src/secret-menu/files/FilesTab';
import BackgroundProcessesTab from 'src/secret-menu/background-processes/BackgroundProcessesTab';
import TenantSettingsTab from 'src/secret-menu/tenant-settings/TenantSettingsTab';
import SharepointTab from 'src/secret-menu/sharepoint/SharepointTab';
import InfoTab from 'src/secret-menu/info/InfoTab';
import SandboxTab from 'src/secret-menu/sandbox/SandboxTab';
import TopicTreeTab from 'src/secret-menu/topic-tree/TopicTreeTab';
import LlmConfigTab from './llm-config/LlmConfigTab';
import ExportAssetsTab from 'src/secret-menu/export-assets/ExportAssetsTab';
import BotMigrationTab from 'src/secret-menu/bot-migration/BotMigrationTab';
import { useFlags } from 'src/feature-flags/FlagsHook';

const SecretMenuTabs = {
  companies: 'companies',
  companyUsers: 'company-users',
  companyClients: 'company-clients',
  templates: 'templates',
  oldTemplates: 'old-templates',
  bulkValuations: 'bulk-valuations',
  files: 'files',
  backgroundProcesses: 'background-processes',
  featureFlags: 'feature-flags',
  editorPlayground: 'editor-playground',
  inlineEdit: 'inline-edit',
  tenantSettings: 'tenant-settings',
  sharepoint: 'sharepoint',
  info: 'info',
  sandbox: 'sandbox',
  topicTree: 'topic-tree',
  llmConfig: 'llm-config',
  exportAssets: 'export-assets',
  botMigration: 'bot-migration',
} as const;

export default function SecretMenuPage() {
  const { tab } = useParams<{ tab: string }>();
  const flags = useFlags();

  const SECRET_MENU_LINKS = [
    {
      label: 'Companies',
      href: '/secret-menu/companies',
    },
    {
      label: 'Company users',
      href: '/secret-menu/company-users',
    },
    {
      label: 'Company clients',
      href: '/secret-menu/company-clients',
    },
    {
      label: 'Templates',
      href: '/secret-menu/templates',
    },
    {
      label: 'Templates (old)',
      href: '/secret-menu/old-templates',
    },
    {
      label: 'Bulk valuations',
      href: '/secret-menu/bulk-valuations',
    },
    {
      label: 'Files',
      href: '/secret-menu/files',
    },
    {
      label: 'Background processes',
      href: '/secret-menu/background-processes',
    },
    {
      label: 'Feature flags',
      href: '/secret-menu/feature-flags',
    },
    {
      label: 'Editor playground',
      href: '/secret-menu/editor-playground',
    },
    {
      label: 'Tenant settings',
      href: '/secret-menu/tenant-settings',
    },
    {
      label: 'Sharepoint (Beta)',
      href: '/secret-menu/sharepoint',
    },
    {
      label: 'Info',
      href: '/secret-menu/info',
    },
    {
      label: 'Sandbox',
      href: '/secret-menu/sandbox',
    },
    {
      label: 'Topic Tree',
      href: '/secret-menu/topic-tree',
    },
    {
      label: 'LLM Config',
      href: '/secret-menu/llm-config',
    },
    {
      label: 'Export Branding',
      href: '/secret-menu/export-assets',
    },
    ...(flags.flagEnabledBotMigration
      ? [
          {
            label: 'Bot Migration',
            href: '/secret-menu/bot-migration',
          },
        ]
      : []),
  ];

  if (!tab) {
    return <Navigate to="/secret-menu/companies" replace />;
  }

  const renderContent = () => {
    switch (tab) {
      case SecretMenuTabs.companies:
        return <CompaniesTab />;
      case SecretMenuTabs.companyUsers:
        return <CompanyUsersPage />;
      case SecretMenuTabs.companyClients:
        return <CompanyClientsPage />;
      case SecretMenuTabs.templates:
        return <TemplatesListPage />;
      case SecretMenuTabs.oldTemplates:
        return <DeprecatedTemplateTab />;
      case SecretMenuTabs.bulkValuations:
        return <UnprocessedValuationUpdatesTab />;
      case SecretMenuTabs.files:
        return <FilesTab />;
      case SecretMenuTabs.backgroundProcesses:
        return <BackgroundProcessesTab />;
      case SecretMenuTabs.featureFlags:
        return <FeatureFlagsTab />;
      case SecretMenuTabs.editorPlayground:
        return <EditorPlaygroundTab />;
      case SecretMenuTabs.tenantSettings:
        return <TenantSettingsTab />;
      case SecretMenuTabs.sharepoint:
        return <SharepointTab />;
      case SecretMenuTabs.info:
        return <InfoTab />;
      case SecretMenuTabs.sandbox:
        return <SandboxTab />;
      case SecretMenuTabs.topicTree:
        return <TopicTreeTab />;
      case SecretMenuTabs.llmConfig:
        return <LlmConfigTab />;
      case SecretMenuTabs.exportAssets:
        return <ExportAssetsTab />;
      case SecretMenuTabs.botMigration:
        return flags.flagEnabledBotMigration ? (
          <BotMigrationTab />
        ) : (
          <Navigate to="/secret-menu/companies" replace />
        );
      default:
        return <Navigate to="/secret-menu/companies" replace />;
    }
  };

  return (
    <Box component="pre">
      <PageWithSidebar links={SECRET_MENU_LINKS} activeUrl={`/company/${tab}`}>
        {renderContent()}
      </PageWithSidebar>
    </Box>
  );
}
